import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';

import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import get from 'lodash/get';

import { uploadAndCreateVideo } from '@src/services/api/videos';
import { formatBytes } from '@src/utils/number-helpers';
import Dropzone from './video_uploader_dropzone';
import Actions from './video_uploader_actions';

const StyledMedia = styled('video')({
    backgroundSize: 'contain',
    objectFit: 'cover',
    width: '100%',
});

const VideoUploader = (props) => {
    const { folderName, onCancel, onVideoUploaded } = props;

    const [videoBlob, setVideoBlob] = useState(null);
    const [videoFile, setVideoFile] = useState(null);
    const [error, setError] = useState(null);
    const [isUploading, setIsUploading] = useState(false);

    const handleFileSelection = useCallback((videoFile) => {
        setVideoFile(videoFile);

        const reader = new FileReader();
        reader.onabort = () => {
            setVideoBlob(null);
            setError('file reading was aborted');
        };

        reader.onerror = () => {
            setVideoBlob(null);
            setError('file reading has failed');
        };

        reader.onload = () => {
            setVideoBlob(reader.result);
            setError(null);
        };

        reader.readAsDataURL(videoFile);
    }, []);

    const handleVideoUploadError = useCallback((error) => {
        let errorMessage = error.messsage;

        const responseMessage = get(error, ['response', 'data', 'error', 'message']);
        if (responseMessage) {
            errorMessage = responseMessage.replace(
                /(\d+(?:((?=\.))\d*))/gm,
                (match) => formatBytes(match),
            );
        }

        setIsUploading(false);
        setVideoBlob(null);
        setError(errorMessage);
    }, [setIsUploading, setVideoBlob, setError]);

    const handleVideoUploadSuccess = useCallback((video) => {
        setIsUploading(false);
        setError(null);

        onVideoUploaded(video);
    }, [setIsUploading, setError, onVideoUploaded]);

    const handleVideoSave = useCallback(async (file) => {
        setIsUploading(true);
        await uploadAndCreateVideo(file, folderName)
            .then((video) => {
                handleVideoUploadSuccess(video);
            }).catch((error) => {
                handleVideoUploadError(error);
            });
    }, [folderName, setIsUploading, handleVideoUploadSuccess, handleVideoUploadError]);

    const handleSaveClick = useCallback((e) => {
        e.preventDefault();

        if (videoFile) {
            handleVideoSave(videoFile);
        } else {
            // eslint-disable-next-line no-alert
            alert('No Video File Selected');
        }
    }, [videoFile, handleVideoSave]);

    return (
        <Card>
            { videoBlob ? null : <Dropzone onFileSelected={handleFileSelection} /> }
            <Grid container sx={{ width: '100%' }} className="" justifyContent="center" alignItems="center">
                <Grid size={6}>
                    {

                        isUploading ?
                            <div className="upload-indication">
                                <img className="loading" src="/img/spinner.gif" height={20} />
                                <Typography variation="caption" align="center" color="primary">
                                    Uploading... Please wait.
                                </Typography>
                            </div> :
                            null
                    }
                    {
                        videoBlob ?
                            <Paper>
                                <StyledMedia>
                                    <source src={URL.createObjectURL(videoFile)} />
                                </StyledMedia>
                            </Paper> :
                            null
                    }
                    {
                        error ?
                            <Typography variation="caption" align="center" color="error">
                                {error}
                            </Typography> :
                            null
                    }
                </Grid>
            </Grid>
            <CardActions>
                <Actions
                    isFileSelected={Boolean(videoBlob)}
                    isUploading={isUploading}
                    onSave={handleSaveClick}
                    onCancel={onCancel}
                />
            </CardActions>
        </Card>
    );
};

VideoUploader.propTypes = {
    folderName: PropTypes.oneOf(['co_branded_videos']).isRequired,
    onCancel: PropTypes.func.isRequired,
    onVideoUploaded: PropTypes.func,
};

export default React.memo(VideoUploader);
